export const LS_BROWSER_NOTIF = 'chatx_browser_notifications'
export const PENDING_DIALOG_OPEN_KEY = 'chatx_pending_dialog_id'
export const OPEN_DIALOG_EVENT = 'chatx:open-dialog'

export function loadBrowserNotificationsEnabled(defaultValue = true): boolean {
  try {
    const value = localStorage.getItem(LS_BROWSER_NOTIF)
    if (value === null) return defaultValue
    return value !== '0'
  } catch {
    return defaultValue
  }
}

export function saveBrowserNotificationsEnabled(enabled: boolean) {
  localStorage.setItem(LS_BROWSER_NOTIF, enabled ? '1' : '0')
}

/**
 * Request notification permission. When permission is still "default", this MUST be
 * invoked synchronously from a user gesture (click) — do not await anything before it.
 */
export function requestBrowserNotificationPermission(): Promise<NotificationPermission> {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return Promise.resolve('denied')
  }
  const current = Notification.permission
  if (current === 'granted' || current === 'denied') {
    return Promise.resolve(current)
  }
  try {
    const result = Notification.requestPermission()
    // Legacy Safari used a callback-only API.
    if (typeof result === 'undefined') {
      return new Promise((resolve) => {
        Notification.requestPermission((permission) => resolve(permission))
      })
    }
    return Promise.resolve(result)
  } catch {
    return Promise.resolve(Notification.permission)
  }
}

export async function ensureBrowserNotificationPermission(): Promise<NotificationPermission> {
  return requestBrowserNotificationPermission()
}

export function setPendingDialogOpen(chatId: string) {
  try {
    sessionStorage.setItem(PENDING_DIALOG_OPEN_KEY, chatId)
  } catch {
    // ignore
  }
}

export function consumePendingDialogOpen(): string | null {
  try {
    const id = sessionStorage.getItem(PENDING_DIALOG_OPEN_KEY)
    if (id) sessionStorage.removeItem(PENDING_DIALOG_OPEN_KEY)
    return id
  } catch {
    return null
  }
}

export function dispatchOpenDialog(chatId: string) {
  setPendingDialogOpen(chatId)
  window.dispatchEvent(new CustomEvent(OPEN_DIALOG_EVENT, { detail: { chatId } }))
}

type BrowserNotificationInput = {
  title: string
  body: string
  chatId?: string
}

export function showBrowserNotification(input: BrowserNotificationInput) {
  if (typeof window === 'undefined' || !('Notification' in window)) return
  if (!loadBrowserNotificationsEnabled()) return
  if (Notification.permission !== 'granted') return
  if (!document.hidden) return

  try {
    const notification = new Notification(input.title, {
      body: input.body,
      icon: '/logo192.png',
      tag: input.chatId ? `chatx-dialog-${input.chatId}` : 'chatx-notification',
    })

    notification.onclick = () => {
      window.focus()
      notification.close()
      if (input.chatId) {
        dispatchOpenDialog(input.chatId)
        if (window.location.pathname !== '/dialogs') {
          window.location.assign('/dialogs')
        }
      }
    }
  } catch (err) {
    console.warn('[browserNotifications] Failed to show notification:', err)
  }
}
